import {
  ANOTHER_MODAL_OPEN,
  ANOTHER_MODAL_CLOSED,
  CLOSE_MODAL,
  OPEN_MODAL,
} from 'redux/modules/common-modal/actions/types';

import { getCommonModal } from 'redux/modules/common-modal/selectors/index';

import { trackModalOpened, trackModalClosed } from 'analytics/modal-tracking';
import { getCustomerId } from 'redux/modules/sessions/selectors';
import { ModalButton } from '@johnlewispartnership/wtr-ingredients/ingredients/Modal/Buttons';
import noop from 'lodash/noop';
import { openNativeDialog } from 'utils/webview/dialog';

export interface IWebviewOptions {
  id: string;
  ctas?: (string | null)[];
  nativeMessage?: string[];
  hasCustomerCareButton?: boolean;
}

export interface IOpenModalArgs {
  asideText?: string;
  buttonOrientation?: 'horizontal' | 'vertical';
  buttons?: ModalButton[];
  cannotClose?: boolean;
  dataTest?: string;
  id?: string;
  messageIsHtml?: boolean;
  messageText?: string[];
  messageTextParams?: {
    [key: string]: boolean | number | string;
  };
  onCloseCallback?: (modalCloseValue?: unknown) => void;
  responseStatus?: number;
  /** This info comes from `modalSeverityValues` in `src/constants/modals.js` */
  severity: 'info' | 'error' | 'conflict';
  titleText?: string;
  useIngredientsModal?: boolean;
  webview?: IWebviewOptions;
}

export interface TypedModalButton<TValue extends ModalButton['buttonValue']> extends ModalButton {
  buttonValue?: TValue;
}

export interface ITypedOpenModalArgs<
  TButtonValue extends ModalButton['buttonValue'] = ModalButton['buttonValue'],
> extends Omit<IOpenModalArgs, 'onCloseCallback'> {
  buttons?: TypedModalButton<TButtonValue>[];
  onCloseCallback?: (modalCloseValue?: TButtonValue) => void;
}

let closeCallback: (modalCloseValue: unknown) => void = noop;

// This function allows the separating of the conflict modal logic to a different file while not changing too much with regards to the
// module level variable with closeCallback and thus reducing risk of the change.
//
// At time of writing, the reason for the separation is the conflict modal logic carries a lot of dependencies that are not needed for the generic modal logic which
// are more frequently used. This separation allows for better code splitting.
export function setCloseCallback(cb: (modalCloseValue: unknown) => void) {
  closeCallback = cb;
}

export function openModal<TButtonValue extends ModalButton['buttonValue']>(
  args: ITypedOpenModalArgs<TButtonValue>,
): WtrThunkAction<void>;
export function openModal(modal: IOpenModalArgs): WtrThunkAction<void> {
  return (dispatch, getState) => {
    const customerId = getCustomerId(getState());

    if (openNativeDialog(modal, customerId)) return;

    const {
      asideText,
      buttonOrientation,
      buttons,
      cannotClose = false,
      dataTest,
      id,
      messageIsHtml = false,
      messageText,
      messageTextParams,
      onCloseCallback,
      responseStatus,
      severity,
      titleText,
      useIngredientsModal,
      webview,
    } = modal;

    closeCallback = onCloseCallback ?? noop;

    trackModalOpened({
      id,
      severity,
      title: titleText,
      customerId,
    });

    dispatch({
      type: OPEN_MODAL,
      conflict: null,
      asideText,
      buttons,
      buttonOrientation,
      cannotClose,
      dataTest,
      id,
      messageIsHtml,
      messageText,
      messageTextParams,
      responseStatus,
      severity,
      titleText,
      useIngredientsModal,
      webview,
    });
  };
}

export const closeModal =
  (
    modalCloseValue: ModalButton['buttonValue'],
    modalCloseText: ModalButton['buttonText'],
  ): WtrThunkAction<void> =>
  (dispatch, getState) => {
    const {
      id,
      severity,
      titleText: title,
    } = getCommonModal(getState() as { modal: unknown }) || {};

    dispatch({ type: CLOSE_MODAL });

    if (closeCallback) {
      setTimeout(() => {
        closeCallback(modalCloseValue);
      }, 0);
    }

    trackModalClosed({
      id,
      severity,
      title,
      response: (modalCloseText || modalCloseValue) as string,
      customerId: getCustomerId(getState()),
    });
  };

export interface NotifyModalArgs {
  id?: string;
  title?: string;
  severity?: IOpenModalArgs['severity'];
  response?: unknown;
}

export const notifyModalOpen =
  ({ id, title, severity }: NotifyModalArgs = {}): WtrThunkAction =>
  (dispatch, getState) => {
    dispatch({ type: ANOTHER_MODAL_OPEN });
    trackModalOpened({
      id,
      severity,
      title,
      customerId: getCustomerId(getState()),
    });
  };

export const notifyModalClosed =
  ({ id, severity, title, response }: NotifyModalArgs = {}): WtrThunkAction =>
  (dispatch, getState) => {
    dispatch({ type: ANOTHER_MODAL_CLOSED });
    trackModalClosed({
      id,
      severity,
      title,
      response: response as string,
      customerId: getCustomerId(getState()),
    });
  };
